import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from '../../utils/axiosConfig';
import { buildApiUrl, showMissingApiUrlAlert } from '../../utils/apiConfig';
import {
    ArrowRight,
    CalendarPlus,
    CheckCircle2,
    CreditCard,
    Image,
    Mail,
    Rocket,
    X
} from 'lucide-react';

const STEP_ICONS = {
    payment: CreditCard,
    profile: Image,
    photos: Image,
    contact: Mail,
    first_publication: CalendarPlus
};

const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

const GlobalOnboardingPrompt = () => {
    const { slug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, loading } = useSelector((state) => state.auth);
    const [status, setStatus] = useState(null);
    const [checking, setChecking] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const forceOpen = new URLSearchParams(location.search).get('onboarding') === '1';
    const isAdminArea = location.pathname.includes('/admin/');
    const dismissedStorageKey = slug ? `passar:onboarding-prompt-dismissed-until:${slug}` : null;

    useEffect(() => {
        let shouldStayDismissed = false;

        if (dismissedStorageKey && typeof window !== 'undefined') {
            try {
                const dismissedUntil = Number(window.localStorage.getItem(dismissedStorageKey));
                shouldStayDismissed = Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
                if (!shouldStayDismissed) window.localStorage.removeItem(dismissedStorageKey);
            } catch (storageError) {
                console.warn('No se pudo leer el estado del aviso de onboarding:', storageError);
            }
        }

        setDismissed(shouldStayDismissed);
    }, [dismissedStorageKey]);

    useEffect(() => {
        if (!slug || loading || !isAuthenticated || isAdminArea) return;

        let alive = true;

        const loadStatus = async () => {
            try {
                setChecking(true);
                const { data } = await axios.get(`/api/clients/${slug}/onboarding`);
                if (!alive) return;
                setStatus(data);
            } catch (err) {
                if (!alive) return;
                setStatus(null);
            } finally {
                if (alive) setChecking(false);
            }
        };

        loadStatus();

        return () => {
            alive = false;
        };
    }, [slug, loading, isAuthenticated, isAdminArea, location.pathname]);

    const pendingSteps = useMemo(() => {
        if (!status?.steps) return [];
        return status.steps.filter(step => !step.completed && step.id !== 'public_page');
    }, [status]);

    const nextStep = pendingSteps[0];
    const shouldShow = Boolean(!isAdminArea && status && !status.completed && pendingSteps.length > 0 && !dismissed);
    const shouldShowLoginPrompt = Boolean(forceOpen && !loading && !isAuthenticated && !isAdminArea && !dismissed);

    const dismissForPage = () => {
        setDismissed(true);

        if (dismissedStorageKey && typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(dismissedStorageKey, String(Date.now() + DISMISS_DURATION_MS));
            } catch (storageError) {
                console.warn('No se pudo guardar el estado del aviso de onboarding:', storageError);
            }
        }

        if (forceOpen) navigate(location.pathname, { replace: true });
    };

    const goToOnboarding = () => {
        setDismissed(true);

        if (nextStep?.actionPath) {
            navigate(nextStep.actionPath);
            return;
        }

        navigate(`/${slug}/admin/dashboard?onboarding=1`);
    };

    const startLogin = () => {
        const url = buildApiUrl(`/api/auth/google?slug=${encodeURIComponent(slug || '')}`);
        if (!url) {
            showMissingApiUrlAlert();
            return;
        }
        window.location.href = url;
    };

    if (checking) return null;

    if (shouldShowLoginPrompt) {
        return (
            <div className="fixed inset-0 z-[1200] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                <div className="w-full max-w-lg bg-surface border border-white/10 rounded-[32px] shadow-2xl overflow-hidden">
                    <div className="h-1 bg-brand-gradient" />
                    <div className="p-6 sm:p-8 relative">
                        <button
                            type="button"
                            onClick={dismissForPage}
                            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/5 text-white/40 hover:text-white flex items-center justify-center"
                            aria-label="Cerrar onboarding"
                        >
                            <X size={18} />
                        </button>

                        <div className="w-14 h-14 rounded-2xl bg-primary-orange/10 text-primary-orange flex items-center justify-center mb-6">
                            <Rocket size={28} />
                        </div>

                        <p className="text-[10px] font-black uppercase tracking-[3px] text-white/30 mb-3">Continuar configuración</p>
                        <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight mb-4">
                            Iniciá sesión para terminar tu configuración
                        </h2>
                        <p className="text-white/45 text-sm font-medium leading-relaxed mb-8">
                            Tu organización ya fue creada. Para mostrarte los pasos pendientes necesitamos confirmar tu cuenta de administrador.
                        </p>

                        <button
                            type="button"
                            onClick={startLogin}
                            className="w-full px-5 py-4 rounded-2xl bg-brand-gradient text-white font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2"
                        >
                            Iniciar sesión y continuar
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!shouldShow) return null;

    return (
        <div className="fixed inset-0 z-[1200] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-surface border border-white/10 rounded-[32px] shadow-2xl overflow-hidden">
                <div className="h-1 bg-brand-gradient" />
                <div className="p-6 sm:p-8 relative">
                    <button
                        type="button"
                        onClick={dismissForPage}
                        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/5 text-white/40 hover:text-white flex items-center justify-center"
                        aria-label="Cerrar onboarding"
                    >
                        <X size={18} />
                    </button>

                    <div className="w-14 h-14 rounded-2xl bg-primary-orange/10 text-primary-orange flex items-center justify-center mb-6">
                        <Rocket size={28} />
                    </div>

                    <p className="text-[10px] font-black uppercase tracking-[3px] text-white/30 mb-3">Configuración pendiente</p>
                    <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight mb-4">
                        Terminemos de preparar tu {status.organizationLabel.toLowerCase()}
                    </h2>
                    <p className="text-white/45 text-sm font-medium leading-relaxed mb-6">
                        Detectamos que todavía faltan pasos clave antes de vender con confianza. Te llevamos directo al próximo paso.
                    </p>

                    <div className="space-y-3 mb-8">
                        {pendingSteps.slice(0, 4).map((step) => {
                            const StepIcon = STEP_ICONS[step.id] || CheckCircle2;
                            return (
                                <div key={step.id} className="flex items-start gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5">
                                    <div className="w-9 h-9 rounded-xl bg-white/5 text-primary-orange flex items-center justify-center shrink-0">
                                        <StepIcon size={18} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-white">{step.label}</p>
                                        <p className="text-xs text-white/35 font-medium mt-1 leading-relaxed">{step.description}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={goToOnboarding}
                            className="flex-1 px-5 py-4 rounded-2xl bg-brand-gradient text-white font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2"
                        >
                            Seguir onboarding
                            <ArrowRight size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={dismissForPage}
                            className="px-5 py-4 rounded-2xl bg-white/5 text-white/50 hover:text-white border border-white/10 font-black uppercase tracking-widest text-xs"
                        >
                            Ver después
                        </button>
                    </div>

                    <p className="mt-5 text-[11px] text-white/25 font-bold">
                        Podés retomarlo desde el panel cuando quieras.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default GlobalOnboardingPrompt;
